import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { useMemo } from 'react';
import { useFeatureFlags } from '@/contexts/FeatureFlagContext';
import { getNavigation } from '@/config/navigation';
import { usePermissions } from '@/hooks/use-permissions';
import { useAuth } from '@/contexts/AuthContext';
import { useDynamicModules, isClientScoped } from '@/api/hooks/useWorkflows';

// Modules that are deny-by-default: hidden from the nav unless the user is a
// platform admin or has been explicitly granted `<module>.view` (they do NOT
// benefit from the "no permissions = allow all" bootstrap fallback).
// `approvals` is strict for the same reason billing is: it is an opt-in
// capability granted per role, and the allow-by-default fallback would show the
// Approval Center to every user on a tenant that has not configured roles yet.
const STRICT_MODULES = new Set<string>(['billing', 'approvals']);

export function NavMain() {
  const { t } = useTranslation('navigation');
  const location = useLocation();
  const features = useFeatureFlags();
  const { canView, canViewStrict } = usePermissions();
  const { user } = useAuth();
  const isAdmin = !!user?.isAdmin;
  // Phase 11 — tenant-authored modules merged into the static nav. The
  // hook returns [] on TOCA tenants without bindings (and on SGS 404),
  // so the legacy left nav is unchanged for them.
  const { data: dynamicModules = [] } = useDynamicModules();
  // Client-centric nav restructure — modules flagged `scope: "client"`
  // live inside the per-client workspace (/clients-hub/:clientId), NOT the
  // main left nav. When any exist, the client_hub module is dropped too:
  // /clients-hub IS the hub for those tenants, so its nav item would
  // duplicate the "Operación de Clientes" entry. Tenants without any
  // client-scoped modules (TOCA/SGS) see the exact same nav as before:
  // both filters are no-ops.
  const mainNavModules = useMemo(() => {
    const hasClientScoped = dynamicModules.some(isClientScoped);
    return dynamicModules.filter(
      (m) => !isClientScoped(m) && !(hasClientScoped && m.moduleType === 'client_hub'),
    );
  }, [dynamicModules]);
  const navGroups = getNavigation(features, mainNavModules);

  // Check if a nav item is active based on current path
  const isItemActive = (itemHref: string, groupItems: typeof navGroups[0]['items']) => {
    const pathname = location.pathname;

    // Exact match
    if (pathname === itemHref) return true;

    // For items that are parent paths (like /maintenance), only match exactly
    // Check if there are other items in the same group that start with this path
    const hasChildItems = groupItems.some(
      (other) => other.href !== itemHref && other.href.startsWith(itemHref + '/')
    );

    if (hasChildItems) {
      // This is a parent path - only match exactly
      return false;
    }

    // For child items, also match sub-paths
    return pathname.startsWith(itemHref + '/');
  };

  return (
    <>
      {navGroups.map((group) => {
        // Filter items by feature flag first (items without `feature` are
        // always eligible), then by permission. The feature check makes the
        // per-tenant runtime overrides effective for items that getNavigation
        // pushes unconditionally (incidents, approvals) — .env.toca was
        // corrected to reflect TOCA's real usage when this filter landed.
        const visibleItems = group.items.filter((item) =>
          (item.feature
            ? (typeof item.feature === 'string' ? [item.feature] : item.feature).every((f) => !!features[f])
            : true)
          && (!item.module
            ? true
            : STRICT_MODULES.has(item.module)
              // Strict modules stay deny-by-default even for admins.
              ? canViewStrict(item.module)
              // Platform admins see every non-strict module, matching
              // PermissionGuard, which already lets them through
              // (`!user?.permissions?.length || user.isAdmin`). Without this the
              // menu HID pages the same admin could open by typing the URL —
              // which is how the missing palletManufacture seed was found, and
              // reads as a broken menu rather than as a permission being absent.
              // Note this grants nothing: those routes were already reachable.
              // It only stops the nav from lying about what is.
              : isAdmin || canView(item.module))
          // Admin-only pages (metadata schemas, system config, workflow
          // designer) stay hidden for non-admin roles even if they hold the
          // gating permission.
          && (!item.adminOnly || isAdmin)
        );

        // Hide group if no visible items
        if (visibleItems.length === 0) return null;

        return (
        <SidebarGroup key={group.titleKey}>
          <SidebarGroupLabel>{t(group.titleKey)}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {visibleItems.map((item) => (
                <SidebarMenuItem key={item.href}>
                  <SidebarMenuButton
                    isActive={isItemActive(item.href, group.items)}
                    tooltip={t(item.titleKey)}
                    asChild
                  >
                    <Link to={item.href}>
                      {item.icon && <item.icon className="size-4" />}
                      <span className="flex-1">{t(item.titleKey)}</span>
                      {/* P08 — pending-work badge on dynamic nav items.
                          Suppressed for null/undefined/zero to avoid noise. */}
                      {typeof item.badge === 'number' && item.badge > 0 && (
                        <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary/15 px-1.5 text-xs font-medium tabular-nums text-primary">
                          {item.badge > 99 ? '99+' : item.badge}
                        </span>
                      )}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        );
      })}
    </>
  );
}
